import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PaperPlaneTilt, Envelope, Clock, Users, Warning, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';

export default function EmailDigest() {
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const load = () => {
    api.post('/digest/preview').then(({ data }) => setPreview(data)).catch(() => {});
    if (isAdmin) api.get('/digest/log').then(({ data }) => setLogs(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMe = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/digest/send-me');
      if (data.sent) toast.success(`تم الإرسال إلى ${user.email}`);
      else toast.info(data.message || 'لا يوجد ما يرسل');
      load();
    } catch (err) { toast.error('فشل: ' + (err.response?.data?.detail || err.message)); }
    finally { setSending(false); }
  };

  const sendAll = async () => {
    if (!window.confirm('إرسال الملخص لجميع الموظفين؟')) return;
    setSendingAll(true);
    try {
      const { data } = await api.post('/digest/send-all');
      toast.success(`تم الإرسال: ${data.sent} / ${data.total_recipients}`);
      load();
    } catch (err) { toast.error('فشل: ' + (err.response?.data?.detail || err.message)); }
    finally { setSendingAll(false); }
  };

  return (
    <div className="space-y-6" data-testid="digest-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">Daily Digest</div>
        <h1 className="text-3xl font-bold text-slate-900">التذكيرات اليومية</h1>
        <p className="text-sm text-slate-500 mt-1">ملخص يومي بالبريد يشمل الفواتير المتأخرة والمهام القريبة</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Stats card */}
          {preview && !preview.empty ? (
            <Card className="p-6 border border-slate-200 rounded-md shadow-none">
              <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">Today's Digest</div>
              <div className="text-lg font-semibold mb-4">{preview.subject}</div>
              <div className="grid grid-cols-4 gap-4 mb-6">
                <StatBox label="متأخرة" value={preview.stats?.overdue} tone="text-red-600" />
                <StatBox label="قريبة" value={preview.stats?.upcoming} tone="text-amber-600" />
                <StatBox label="مهام" value={preview.stats?.tasks} tone="text-blue-600" />
                <StatBox label="مبلغ متأخر" value={Number(preview.stats?.total_overdue_amount || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} tone="text-slate-900" />
              </div>
              <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
                <div className="text-xs text-slate-500 px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                  <Envelope size={14} /> معاينة البريد
                </div>
                <iframe
                  title="digest-preview"
                  srcDoc={preview.html}
                  className="w-full"
                  style={{ height: '520px', border: 'none' }}
                  data-testid="digest-preview-iframe"
                />
              </div>
            </Card>
          ) : (
            <Card className="p-8 border border-slate-200 rounded-md shadow-none text-center">
              <Info size={40} className="mx-auto text-slate-400 mb-3" />
              <div className="text-slate-600">لا يوجد محتوى في الملخص اليومي حالياً — استورد فواتير Yardi وأضف مهام لتفعيل التذكيرات.</div>
            </Card>
          )}
        </div>

        {/* Actions sidebar */}
        <div className="space-y-4">
          <Card className="p-5 border border-slate-200 rounded-md shadow-none">
            <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-3">Send</div>
            <div className="space-y-2">
              <Button onClick={sendMe} disabled={sending || preview?.empty} className="w-full bg-slate-900 text-white" data-testid="send-me-button">
                <PaperPlaneTilt className="ms-2" size={16} /> {sending ? 'جاري الإرسال...' : `أرسل لي (${user?.email})`}
              </Button>
              {isAdmin && (
                <Button onClick={sendAll} disabled={sendingAll} variant="outline" className="w-full" data-testid="send-all-button">
                  <Users className="ms-2" size={16} /> {sendingAll ? 'جاري الإرسال...' : 'أرسل لكل الموظفين'}
                </Button>
              )}
            </div>
          </Card>

          <Card className="p-5 border border-slate-200 rounded-md shadow-none">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={16} className="text-[#B4935A]" />
              <div className="text-sm font-semibold text-slate-900">جدولة يومية</div>
            </div>
            <p className="text-xs text-slate-600 leading-6">
              لإرسال الملخص تلقائياً كل يوم، أضف الطلب أدناه إلى cron الخادم (مثل الساعة 8 صباحاً):
            </p>
            <div className="mt-3 text-xs mono bg-slate-900 text-slate-100 rounded-md p-3 leading-6 overflow-x-auto" dir="ltr">
              0 8 * * * curl -X POST \<br />
              &nbsp;&nbsp;{`${window.location.origin}/api/digest/send-all`} \<br />
              &nbsp;&nbsp;-H "Authorization: Bearer ADMIN_TOKEN"
            </div>
          </Card>

          {isAdmin && logs.length > 0 && (
            <Card className="p-5 border border-slate-200 rounded-md shadow-none">
              <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-3">Recent Sends</div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {logs.slice(0, 20).map(l => (
                  <div key={l.id} className="flex items-center justify-between text-xs pb-2 border-b border-slate-100">
                    <div className="min-w-0 flex-1">
                      <div className="mono text-slate-700 truncate">{l.email}</div>
                      <div className="text-slate-500 mono">{new Date(l.sent_at).toLocaleString('ar-SA')}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] ms-2">{l.trigger}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

const StatBox = ({ label, value, tone }) => (
  <div className="border border-slate-200 rounded-md p-3 text-center">
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`text-2xl font-bold tabular-nums mt-1 ${tone}`}>{value ?? 0}</div>
  </div>
);
